import { Button } from "@babackapp/ui/components/button";
import { FloatingBar, FloatingBarGroup } from "@babackapp/ui/components/floating-panel";
import { AnimatePresence, motion } from "motion/react";
import { useFormContext } from "react-hook-form";
import { EmailTemplateFormData } from "../types";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { LoaderIcon, Redo2, Trash2, Undo2 } from "lucide-react";
import { ConfirmationModal } from "./confirmation-modal";
import { useTemplatesEditorTemplates } from "../context/templates-editor-templates-context";
import useTemplateValidation from "../hooks/use-template-validation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { ValidationErrorsDisplay } from "./validation-errors-display";
import { useEditorHistoryContext } from "@babackapp/blocks-editor/contexts/editor-history-context";


export function BottomBar() {
  const { updateTemplate, currentTemplate, removeTemplate } = useTemplatesEditorTemplates();
  const { t } = useTranslation("trackoffice");
  const form = useFormContext<EmailTemplateFormData>();
  const isDirty = form.formState.isDirty;
  const isSaving = form.formState.isSubmitting;
  const { validateTemplate } = useTemplateValidation();
  const { undo, redo, canUndo, canRedo, captureState } = useEditorHistoryContext();

  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Array<{
    type: "missing_translation" | "missing_subject_translation";
    severity: "error" | "warning";
    blockId?: string;
  }>>([]);

  const handleResetConfirm = async () => {
    try {
      if (currentTemplate?.id) {
        removeTemplate(currentTemplate.id);
      }
    } catch (error) {
      console.error("Failed to delete template:", error);
    }
  };

  const onClear = () => {
    form.setValue("subject", {}, { shouldDirty: true });
    form.setValue(
      "content",
      {
        afterBlocks: [],
        beforeBlocks: [],
        insideBlocks: [],
      } as unknown as EmailTemplateFormData["content"],
      { shouldDirty: true },
    );
    form.setValue("assets", { uploads: {} }, { shouldDirty: true });
    form.setValue("assets", { uploads: {} }, { shouldDirty: true });

    // Capture state AFTER clearing for undo/redo
    setTimeout(() => captureState(), 0);
  };


  function handleSave(data: EmailTemplateFormData) {
    const validationErrors = validateTemplate(data);

    // const hasErrors = res.some(item => item.severity === "error");
    const hasErrors = !!validationErrors.length;

    if (hasErrors) {
      setValidationErrors(validationErrors);
      setIsValidationModalOpen(true);
      return;
    }

    updateTemplate(data);
  }


  const handleSaveAnyway = () => {
    const data = form.getValues();
    updateTemplate(data);
    setIsValidationModalOpen(false);
  };


  const _onSave = form.handleSubmit(handleSave);

  return (
    <>
      <div className="w-full flex items-center justify-center">
        <FloatingBarGroup>
          <FloatingBar>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={undo}
                disabled={!canUndo}
                className="rounded-full shadow-none"
                title="Undo"
              >
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={redo}
                disabled={!canRedo}
                className="rounded-full shadow-none"
                title="Redo"
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex">
              <Button
                variant="outline"
                size="sm"
                onClick={onClear}
                className="flex rounded-full items-center gap-2 shadow-none"
              >
                <Trash2 className="h-4 w-4" />
                {t(
                  "notificationsTemplatesEditor.emailEditor.actions.clear",
                  "Clear",
                )}
              </Button>
              {/* <TemplateResetActionsButton
               onReset={handleResetClick}
               onSelectTemplate={handleTemplateSelection}
               />*/}
            </div>
            <AnimatePresence>
              {isDirty && (
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "auto", opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <Button
                    size="sm"
                    className="rounded-full whitespace-nowrap"
                    onClick={_onSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <LoaderIcon className="animate-spin" />
                    ) : (
                      t(
                        "notificationsTemplatesEditor.emailEditor.actions.save",
                        "Save",
                      )
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </FloatingBar>
        </FloatingBarGroup>
      </div>
      <ConfirmationModal
        open={isResetModalOpen}
        onOpenChange={setIsResetModalOpen}
        title={t(
          "notificationsTemplatesEditor.emailEditor.actions.resetModal.title",
          "Charger le template par défaut",
        )}
        message={t(
          "notificationsTemplatesEditor.emailEditor.actions.resetModal.message",
          "Toutes vos modifications seront perdues. Souhaitez-vous continuer ?",
        )}
        cancelText={t(
          "notificationsTemplatesEditor.emailEditor.actions.resetModal.cancel",
          "Annuler",
        )}
        confirmText={t(
          "notificationsTemplatesEditor.emailEditor.actions.resetModal.confirm",
          "Réinitialiser le template",
        )}
        onConfirm={handleResetConfirm}
      />

      <Dialog open={isValidationModalOpen} onOpenChange={setIsValidationModalOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>
              {t(
                "notificationsTemplatesEditor.validation.modal.title",
                "Template Validation Errors",
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "notificationsTemplatesEditor.validation.modal.description",
                "Please fix the following errors before saving the template.",
              )}
            </DialogDescription>
          </DialogHeader>
          <ValidationErrorsDisplay validationItems={validationErrors} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsValidationModalOpen(false)}>
              {t("common.close", "Close")}
            </Button>
            <Button onClick={handleSaveAnyway}>
              {t(
                "notificationsTemplatesEditor.validation.modal.saveAnyway",
                "Save Anyway",
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
